import { useMemo, useState } from 'preact/hooks';
import { useApi } from '../../hooks/useApi.js';
import { Button } from '../../components/Button.jsx';
import { STATUS_LABEL, STATUS_PILL, TAN } from './equipment-shared.js';
import { EquipmentEditModal } from './EquipmentEditModal.jsx';

const StatusPill = ({ status }) => (
  <span class={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_PILL[status] ?? STATUS_PILL.active}`}>
    <span class="block h-1.5 w-1.5 rounded-full bg-current" />
    {STATUS_LABEL[status] ?? status}
  </span>
);

const EquipmentCard = ({ item, onEdit }) => (
  <button
    type="button"
    onClick={() => onEdit(item)}
    class="block w-full rounded-lg border border-border-subtle bg-bg-card p-4 text-left transition hover:border-border-strong hover:bg-bg-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
  >
    <div class="mb-2.5 flex items-center justify-between">
      <StatusPill status={item.status} />
      {item.categoryName && (
        <span
          class="text-[10px] font-semibold uppercase tracking-eyebrow"
          style={{ color: item.categoryColor || TAN }}
        >
          {item.categoryName}
        </span>
      )}
    </div>
    <p class="m-0 mb-1 font-serif text-[18px] font-medium tracking-tight text-text-primary">{item.name}</p>
    <p class="m-0 text-[12px] font-medium text-text-tertiary">
      {item.model || <span class="text-text-muted">No model</span>}
      {item.location && <span class="ml-2">· {item.location}</span>}
      {item.ipAddress && <span class="ml-2 font-mono text-text-body">{item.ipAddress}</span>}
    </p>
  </button>
);

export const EquipmentPage = () => {
  const { data: equipment, loading, error, refetch } = useApi('/equipment');
  const { data: categories, refetch: refetchCategories } = useApi('/equipment/categories');

  const refetchAll = () => { refetch(); refetchCategories(); };

  const [categoryFilter, setCategoryFilter] = useState('all');
  const [newOpen, setNewOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const counts = useMemo(() => {
    const acc = { all: 0 };
    (equipment ?? []).forEach((e) => {
      acc.all += 1;
      const key = e.categoryId ?? 'none';
      acc[key] = (acc[key] ?? 0) + 1;
    });
    return acc;
  }, [equipment]);

  const filtered = useMemo(() => {
    if (!equipment) return [];
    if (categoryFilter === 'all') return equipment;
    if (categoryFilter === 'none') return equipment.filter((e) => e.categoryId == null);
    return equipment.filter((e) => String(e.categoryId) === categoryFilter);
  }, [equipment, categoryFilter]);

  return (
    <div>
      <section class="mb-6 flex items-start justify-between gap-4">
        <div>
          <div class="mb-1.5 flex items-center gap-2">
            <span class="block h-2.5 w-2.5 rounded-full" style={{ background: TAN }} />
            <span class="text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: TAN }}>
              Equipment
            </span>
          </div>
          <h1 class="m-0 mb-1.5 text-[30px] leading-[1.18] tracking-tight text-text-primary">
            Machines &amp; equipment
          </h1>
          <p class="m-0 text-sm text-text-secondary">
            Laminators, workstations, label printers, and anything else non-printer that needs tracking.
          </p>
        </div>
        <Button variant="secondary" onClick={() => setNewOpen(true)}>+ Add equipment</Button>
      </section>

      <div class="mb-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setCategoryFilter('all')}
          class={`rounded-full border px-3 py-1 text-[11px] font-semibold ${categoryFilter === 'all' ? 'border-brand bg-brand/[0.12] text-brand' : 'border-border-strong text-text-tertiary hover:bg-bg-card'}`}
        >
          All · {counts.all ?? 0}
        </button>
        {(categories ?? []).map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setCategoryFilter(String(c.id))}
            class={`rounded-full border px-3 py-1 text-[11px] font-semibold ${categoryFilter === String(c.id) ? 'border-brand bg-brand/[0.12] text-brand' : 'border-border-strong text-text-tertiary hover:bg-bg-card'}`}
          >
            {c.name} · {counts[c.id] ?? 0}
          </button>
        ))}
        {counts.none > 0 && (
          <button
            type="button"
            onClick={() => setCategoryFilter('none')}
            class={`rounded-full border px-3 py-1 text-[11px] font-semibold ${categoryFilter === 'none' ? 'border-brand bg-brand/[0.12] text-brand' : 'border-border-strong text-text-tertiary hover:bg-bg-card'}`}
          >
            No category · {counts.none}
          </button>
        )}
      </div>

      {loading && <p class="text-sm text-text-tertiary">Loading…</p>}
      {error && (
        <p class="text-sm font-semibold text-ink-magenta">Failed to load: {error.message}</p>
      )}

      {filtered.length > 0 && (
        <div class="grid grid-cols-1 gap-2.5 md:grid-cols-2">
          {filtered.map((e) => <EquipmentCard key={e.id} item={e} onEdit={setEditing} />)}
        </div>
      )}
      {equipment && filtered.length === 0 && (
        <div class="surface-card p-6 text-center">
          <p class="m-0 font-serif text-lg font-medium text-text-primary">No equipment yet.</p>
          <p class="m-0 mt-1 text-sm text-text-secondary">
            Click "+ Add equipment" above to record a laminator, workstation, or other machine.
          </p>
        </div>
      )}

      <EquipmentEditModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onSaved={() => { setNewOpen(false); refetchAll(); }}
      />
      <EquipmentEditModal
        open={!!editing}
        equipment={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); refetchAll(); }}
      />
    </div>
  );
};
